import axios from 'axios';
import jsonpipe from 'jsonpipe';
import config from '../../config';
import { showError, showSlotOverlappingWarning } from '../utils';
import { store } from '../store';
import moment from 'moment';
import { setAuthToken } from '../actions';

const redirectForLogin = () => {
  window.location.href = '/';
};

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const statusCode = error.response ? error.response.data : error;
    if (statusCode === 401) {
      store.dispatch(setAuthToken(''));
      return redirectForLogin();
    }

    return Promise.reject(error);
  }
);

// basic profile screen
export function fetchBasicProfile(doctorId) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  return axios
    .get(
      `${config.apiBaseUrl}/network/practice/doctor-basic-profile/${doctorId}`,
      {
        headers,
      }
    )
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function uploadProfilePicture(
  file,
  doctorId,
  onUploadProgress,
  authToken
) {
  const headers = {
    authorization: authToken,
  };

  const formData = new FormData();
  formData.append('file', file);
  // formData.append('preview', file);

  return axios
    .post(
      `${config.apiBaseUrl}/network/practice/doctor-basic-profile/${doctorId}/profileImage`,
      formData,
      {
        headers,
        onUploadProgress,
      }
    )
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      } else {
        const err = new Error('Invalid response');
        err.data = responseBody;
        showError(err);
        throw err;
      }
    })
    .catch((err) => {
      showError(err);
      throw err;
    });
}

export function uploadNetworkProfilePicture(
  file,
  centerId,
  onUploadProgress,
  authToken
) {
  const headers = {
    authorization: authToken,
  };

  const formData = new FormData();
  formData.append('file', file);
  // formData.append('preview', file);

  return axios
    .post(
      `${config.apiBaseUrl}/network/practice/center/${centerId}/profileImage`,
      formData,
      {
        headers,
        onUploadProgress,
      }
    )
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      } else {
        const err = new Error('Invalid response');
        err.data = responseBody;
        showError(err);
        throw err;
      }
    })
    .catch((err) => {
      showError(err);
      throw err;
    });
}

export function uploadSignatureImage(
  file,
  doctorId,
  onUploadProgress,
  authToken
) {
  const headers = {
    authorization: authToken,
  };

  const formData = new FormData();
  formData.append('file', file);
  formData.append('doctorId', doctorId);
  // formData.append('preview', file);

  return axios
    .post(
      `${config.apiBaseUrl}/network/practice/doctor-basic-profile/signature`,
      formData,
      {
        headers,
        onUploadProgress,
      }
    )
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      } else {
        const err = new Error('Invalid response');
        err.data = responseBody;
        showError(err);
        throw err;
      }
    })
    .catch((err) => {
      showError(err);
      throw err;
    });
}

export function createBasicProfile({
  doctorName,
  doctorEmail,
  doctorAddress,
  doctorPhone,
  doctorPassword,
  doctorGender,
  doctorDescription,
  doctorQualifications,
  practiceYears,
  isOnlineDoctor,
  isOfflineDoctor,
  doctorVerticalId,
  doctorSpeciality,
  knownLanguages,
  isNightDoctor,
  networkSource,
  preference,
  onlineSlotDuration,
  onlyAvailableForBookForLater,
  scheduleConsultCancelWindowEnds,
  onlineSlotCalcWindow,
  isHomeAppointmentAvailable,
  homeAppointmentRange,
  latitude,
  longitude,
  homeAppointmentCharges,
  referenceId,
  accessForQcDashboard,
  city,
}) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  const body = {
    doctorName,
    doctorEmail,
    doctorAddress,
    doctorPhone,
    password: doctorPassword,
    doctorGender,
    doctorDescription,
    doctorQualifications,
    practiceYears,
    isOnlineDoctor,
    isOfflineDoctor,
    doctorVerticalId,
    doctorSpeciality,
    knownLanguages,
    isNightDoctor,
    networkSource,
    preference,
    onlineSlotDuration,
    onlyAvailableForBookForLater,
    scheduleConsultCancelWindowEnds,
    onlineSlotCalcWindow,
    isHomeAppointmentAvailable,
    homeAppointmentRange,
    latitude,
    longitude,
    homeAppointmentCharges,
    referenceId,
    accessForQcDashboard,
    city,
  };
  return axios
    .post(
      `${config.apiBaseUrl}/network/practice/doctor-basic-profile/create`,
      body,
      {
        headers,
      }
    )
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function updateBasicProfile({
  doctorId,
  doctorName,
  doctorEmail,
  doctorAddress,
  doctorPhone,
  doctorPassword,
  doctorGender,
  doctorDescription,
  doctorQualifications,
  practiceYears,
  isOnlineDoctor,
  isOfflineDoctor,
  doctorVerticalId,
  doctorSpeciality,
  knownLanguages,
  isNightDoctor,
  networkSource,
  preference,
  onlineSlotDuration,
  onlyAvailableForBookForLater,
  scheduleConsultCancelWindowEnds,
  onlineSlotCalcWindow,
  isHomeAppointmentAvailable,
  homeAppointmentRange,
  latitude,
  longitude,
  homeAppointmentCharges,
  referenceId,
  accessForQcDashboard,
  city,
}) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  const body = {
    doctorId,
    doctorName,
    doctorEmail,
    doctorAddress,
    doctorPhone,
    doctorGender,
    doctorDescription,
    password: doctorPassword,
    doctorQualifications,
    practiceYears,
    isOnlineDoctor,
    isOfflineDoctor,
    doctorVerticalId,
    doctorSpeciality,
    knownLanguages,
    isNightDoctor,
    networkSource,
    preference,
    onlineSlotDuration,
    onlyAvailableForBookForLater,
    scheduleConsultCancelWindowEnds,
    onlineSlotCalcWindow,
    isHomeAppointmentAvailable,
    homeAppointmentRange,
    latitude,
    longitude,
    homeAppointmentCharges,
    referenceId,
    accessForQcDashboard,
    city,
  };
  return axios
    .post(
      `${config.apiBaseUrl}/network/practice/doctor-basic-profile/${doctorId}`,
      body,
      {
        headers,
      }
    )
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function updateDoctorActiveStatus(doctorId, status) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  const body = {
    status,
  };
  return axios
    .post(
      `${config.apiBaseUrl}/network/practice/doctor-basic-profile/${doctorId}/status`,
      body,
      {
        headers,
      }
    )
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

// doctor education screen
export function fetchDoctorEducation(doctorId) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  return axios
    .get(`${config.apiBaseUrl}/network/practice/doctor-education/${doctorId}`, {
      headers,
    })
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function fetchCortisBillingEntitities(search) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  return axios
    .get(
      `${config.apiBaseUrl}/network/network-center/billing-entities?search=${search}`,
      {
        headers,
      }
    )
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function deleteEducation(doctorId, doctorEducationId) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  return axios
    .post(
      `${config.apiBaseUrl}/network/practice/doctor-education/${doctorId}/delete`,
      {
        doctorEducationId,
      },
      {
        headers,
      }
    )
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function updateEducation({
  doctorId,
  doctorEducationId,
  title,
  course,
  institution,
  passingYear,
  fromDate,
  toDate,
}) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  const body = {
    doctorId,
    title,
    course,
    institution,
    passingYear,
    fromDate,
    toDate,
  };
  if (doctorEducationId) {
    body.doctorEducationId = doctorEducationId;
  }
  return axios
    .post(
      `${config.apiBaseUrl}/network/practice/doctor-education/${doctorId}`,
      body,
      {
        headers,
      }
    )
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

// doctor experience screen
export function fetchDoctorExperience(doctorId) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  return axios
    .get(
      `${config.apiBaseUrl}/network/practice/doctor-experience/${doctorId}`,
      {
        headers,
      }
    )
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function deleteExperience(doctorId, doctorExperienceId) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  return axios
    .post(
      `${config.apiBaseUrl}/network/practice/doctor-experience/${doctorId}/delete`,
      {
        doctorExperienceId,
      },
      {
        headers,
      }
    )
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function updateExperience({
  doctorId,
  doctorExperienceId,
  department,
  designation,
  location,
  current,
  fromDate,
  toDate,
}) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  const body = {
    doctorId,
    department,
    designation,
    location,
    current,
    fromDate,
    toDate,
  };
  if (doctorExperienceId) {
    body.doctorExperienceId = doctorExperienceId;
  }
  return axios
    .post(
      `${config.apiBaseUrl}/network/practice/doctor-experience/${doctorId}`,
      body,
      {
        headers,
      }
    )
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

// doctor membership screen
export function fetchDoctorMemberships(doctorId) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  return axios
    .get(
      `${config.apiBaseUrl}/network/practice/doctor-membership/${doctorId}`,
      {
        headers,
      }
    )
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function deleteMembership(doctorId, doctorMembershipId) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  return axios
    .post(
      `${config.apiBaseUrl}/network/practice/doctor-membership/${doctorId}/delete`,
      {
        doctorMembershipId,
      },
      {
        headers,
      }
    )
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function updateMembership({
  doctorId,
  doctorMembershipId,
  title,
  institution,
  fromDate,
  toDate,
}) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  const body = {
    doctorId,
    title,
    institution,
    fromDate,
    toDate,
  };
  if (doctorMembershipId) {
    body.doctorMembershipId = doctorMembershipId;
  }
  return axios
    .post(
      `${config.apiBaseUrl}/network/practice/doctor-membership/${doctorId}`,
      body,
      {
        headers,
      }
    )
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

// doctor awards screen
export function fetchDoctorAwards(doctorId) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  return axios
    .get(`${config.apiBaseUrl}/network/practice/doctor-awards/${doctorId}`, {
      headers,
    })
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function deleteAward(doctorId, doctorAwardId) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  return axios
    .post(
      `${config.apiBaseUrl}/network/practice/doctor-awards/${doctorId}/delete`,
      {
        doctorAwardId,
      },
      {
        headers,
      }
    )
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function updateAward({
  doctorId,
  doctorAwardId,
  title,
  remark,
  fromDate,
  toDate,
}) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  const body = {
    doctorId,
    title,
    remark,
    fromDate,
    toDate,
  };
  if (doctorAwardId) {
    body.doctorAwardId = doctorAwardId;
  }
  return axios
    .post(
      `${config.apiBaseUrl}/network/practice/doctor-awards/${doctorId}`,
      body,
      {
        headers,
      }
    )
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

// doctor registrations screen
export function fetchDoctorRegistrations(doctorId) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  return axios
    .get(
      `${config.apiBaseUrl}/network/practice/doctor-registrations/${doctorId}`,
      {
        headers,
      }
    )
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function deleteRegistration(doctorId, doctorRegistrationId) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  return axios
    .post(
      `${config.apiBaseUrl}/network/practice/doctor-registrations/${doctorId}/delete`,
      {
        doctorRegistrationId,
      },
      {
        headers,
      }
    )
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function updateRegistration({
  doctorId,
  doctorRegistrationId,
  title,
  year,
}) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  const body = {
    doctorId,
    title,
    year,
  };
  if (doctorRegistrationId) {
    body.doctorRegistrationId = doctorRegistrationId;
  }
  return axios
    .post(
      `${config.apiBaseUrl}/network/practice/doctor-registrations/${doctorId}`,
      body,
      {
        headers,
      }
    )
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

// doctor services screen
export function fetchDoctorServices(doctorId) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  return axios
    .get(`${config.apiBaseUrl}/network/practice/doctor-services/${doctorId}`, {
      headers,
    })
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function deleteService(doctorId, doctorServiceId) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  return axios
    .post(
      `${config.apiBaseUrl}/network/practice/doctor-services/${doctorId}/delete`,
      {
        doctorServiceId,
      },
      {
        headers,
      }
    )
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function updateService({ doctorId, doctorServiceId, title }) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  const body = {
    doctorId,
    title,
  };
  if (doctorServiceId) {
    body.doctorServiceId = doctorServiceId;
  }
  return axios
    .post(
      `${config.apiBaseUrl}/network/practice/doctor-services/${doctorId}`,
      body,
      {
        headers,
      }
    )
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

// doctor specialization screen
export function fetchDoctorSpecializations(doctorId) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  return axios
    .get(
      `${config.apiBaseUrl}/network/practice/doctor-specialization/${doctorId}`,
      {
        headers,
      }
    )
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function deleteSpecialization(doctorId, doctorSpecializationId) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  return axios
    .post(
      `${config.apiBaseUrl}/network/practice/doctor-specialization/${doctorId}/delete`,
      {
        doctorSpecializationId,
      },
      {
        headers,
      }
    )
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function updateSpecialization({
  doctorId,
  doctorSpecializationId,
  title,
  specialist,
  specialistType,
  specialistDefinition,
  commonName,
}) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  const body = {
    doctorId,
    title,
    specialist,
    specialistType,
    specialistDefinition,
    commonName,
  };
  if (doctorSpecializationId) {
    body.doctorSpecializationId = doctorSpecializationId;
  }
  return axios
    .post(
      `${config.apiBaseUrl}/network/practice/doctor-specialization/${doctorId}`,
      body,
      {
        headers,
      }
    )
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

// doctor practice center screen
export function fetchDoctorPracticeCenters(doctorId) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  return axios
    .get(`${config.apiBaseUrl}/network/practice/doctor-practice/${doctorId}`, {
      headers,
    })
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function updatePracticeCenter({
  doctorId,
  centerId,
  active,
  opdCharges,
  onlineOpdCharges,
  offlineDiscount,
  offlineDiscountOperation,
  onlineDiscount,
  onlineDiscountOperation,
  fDiscount,
  fDiscountOperation,
  fDays,
  onlineFDiscount,
  onlineFDiscountOperation,
  onlineFDays,
  fulfilmentType,
  status,
  onlineCorporatePayout,
  onlineRetailPayout,
  telehealth,
  priceChangeReason,
  onboardingRequestId,
}) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  const body = {
    doctorId,
    active: active ? 1 : 0,
    centerId,
    opdCharges,
    onlineOpdCharges,
    offlineDiscount,
    offlineDiscountOperation,
    onlineDiscount,
    onlineDiscountOperation,
    fDiscount,
    fDiscountOperation,
    fDays,
    onlineFDiscount,
    onlineFDiscountOperation,
    onlineFDays,
    fulfilmentType,
    status,
    onlineCorporatePayout,
    onlineRetailPayout,
    telehealth,
    priceChangeReason,
    finalOpdCharges: opdCharges,
    onboardingRequestId,
  };
  return axios
    .post(`${config.apiBaseUrl}/network/practice/doctor-practice`, body, {
      headers,
    })
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function sendRequestForDoctorDetailsChanges({
  doctorId,
  centerId,
  centerName,
  active,
  opdCharges,
  onlineOpdCharges,
  offlineDiscount,
  offlineDiscountOperation,
  onlineDiscount,
  onlineDiscountOperation,
  fDiscount,
  fDiscountOperation,
  fDays,
  onlineFDiscount,
  onlineFDiscountOperation,
  onlineFDays,
  fulfilmentType,
  status,
  onlineCorporatePayout,
  onlineRetailPayout,
  telehealth,
  priceChangeReason,
  previousData,
  requestType,
}) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  const body = {
    doctorId,
    active: active ? 1 : 0,
    centerName,
    centerId,
    opdCharges,
    onlineOpdCharges,
    offlineDiscount,
    offlineDiscountOperation,
    onlineDiscount,
    onlineDiscountOperation,
    fDiscount,
    fDiscountOperation,
    fDays,
    onlineFDiscount,
    onlineFDiscountOperation,
    onlineFDays,
    fulfilmentType,
    status,
    onlineCorporatePayout,
    onlineRetailPayout,
    telehealth,
    priceChangeReason,
    previousData,
    requestType,
  };
  return axios
    .post(
      `${config.apiBaseUrl}/network/practice/doctor-practice/send-approval`,
      body,
      {
        headers,
      }
    )
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      err.data.errorTitle = 'Active Price Change Request';
      showError(err);
      throw err;
    });
}

export function updatePracticeCenterActiveStatus(
  doctorId,
  doctorPracticeId,
  status
) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  const body = {
    doctorId,
    centerId: doctorPracticeId,
    active: status ? 1 : 0,
  };
  return axios
    .post(
      `${config.apiBaseUrl}/network/practice/doctor-practice/status`,
      body,
      {
        headers,
      }
    )
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function fetchDoctorSlots(doctorId) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  return axios
    .get(`${config.apiBaseUrl}/network/practice/doctor-slots/${doctorId}`, {
      headers,
    })
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function removePracticeForDoctor(doctorId, centerId) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  const body = {
    doctorId,
    centerId,
  };
  return axios
    .delete(`${config.apiBaseUrl}/network/practice/doctor-practice`, {
      data: body,
      headers,
    })
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function getDoctorPriceHistory(doctorId, centerId) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  const body = {
    doctorId,
    centerId,
  };

  return axios
    .post(
      `${config.apiBaseUrl}/network/practice/doctor-practice/price-history`,
      body,
      {
        headers,
      }
    )
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function fetchCenters(search) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  return axios
    .get(`${config.apiBaseUrl}/network/practice/center`, {
      params: {
        q: search,
      },
      headers,
    })
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function fetchDoctors(search) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  return axios
    .get(`${config.apiBaseUrl}/network/practice/doctors`, {
      params: {
        q: search,
      },
      headers,
    })
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function deleteDoctorSlot(slotId, centerId, doctorId) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  const body = {
    doctorSlotId: slotId,
    networkCenterId: centerId,
  };
  console.log({ slotId, centerId, doctorId, authToken }, 'deleteDoctorSlot');
  return axios
    .delete(`${config.apiBaseUrl}/network/practice/doctor-slots/${doctorId}`, {
      headers,
      data: body,
    })
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function persistDoctorSlots(doctorId, slots, postWorkingHours) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };

  return axios
    .post(
      `${config.apiBaseUrl}/network/practice/doctor-slots/${doctorId}`,
      { slots, postWorkingHours },
      {
        headers,
      }
    )
    .then((resp) => {
      const responseBody = resp.data;
      console.log(`responseBody ${responseBody}`);
      if (responseBody.message === 'success') {
        return responseBody;
      }
      if (responseBody.message === 'warning') {
        // return 'some slots are overlapping';
        console.log('inside warning');
        showSlotOverlappingWarning(responseBody.warningData);
        return;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function sendWelcomeMailToCenter(centerId, userName, password) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  return axios
    .post(
      `${config.apiBaseUrl}/network/practice/center/welcome-mailer-trigger`,
      { userName, password, centerId },
      {
        headers,
      }
    )
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function fetchCenterBasicProfile(centerId) {
  const { authToken } = store.getState().user;
  console.log('\nuser:', store.getState().user);
  const headers = {
    authorization: authToken,
  };
  return axios
    .get(`${config.apiBaseUrl}/network/practice/center/${centerId}`, {
      headers,
    })
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function fetchMoUDetailsForCenter(centerId) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  return axios
    .get(`${config.apiBaseUrl}/network/practice/center/mou/${centerId}`, {
      headers,
    })
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      } else if (responseBody.message === 'No MoU') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function deleteMouForCenter(mouId) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  const params = {
    mouId,
  };
  return axios
    .delete(`${config.apiBaseUrl}/network/practice/center/mou/delete`, {
      headers,
      params,
    })
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      } else if (responseBody.message === 'No MoU') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function saveMouCategoryCenter(payload) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  const body = {
    centerId: payload.centerId,
    mouStartDate: payload.mouStartDate,
    documentCategory: payload.documentCategory,
    documentType: payload.documentType,
  };
  return axios
    .post(`${config.apiBaseUrl}/network/practice/center/add-documents`, body, {
      headers,
    })
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function saveMouDetailsForCenter(payload) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  const formattedStartDate = moment(payload.mouStartDate).format('YYYY-MM-DD');
  const formattedEndDate = moment(payload.mouEndDate).format('YYYY-MM-DD');
  console.log('formattedStartDate: ', formattedStartDate);
  console.log('formattedEndDate: ', formattedEndDate);
  const body = {
    centerId: payload.centerId,
    mouCategory: payload.mouCategory,
    mouStatus: payload.mouStatus,
    mouStartDate: formattedStartDate,
    mouEndDate: formattedEndDate,
    mouName: payload.mouName,
    mouLogIds: payload.finalMouIds,
    documentCategory: payload.documentCategory,
    documentType: payload.documentType,
    panNumber: payload.panNumber ? payload.panNumber.toUpperCase() : null,
    panName: payload.panName ? payload.panName : null,
  };

  return axios
    .post(`${config.apiBaseUrl}/network/practice/center/add-mou`, body, {
      headers,
    })
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function fetchCenterStates() {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };

  return axios
    .get(`${config.apiBaseUrl}/network/center/states`, {
      headers,
    })
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function addNetworkSpoc(spocDetails) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  const body = {
    centerId: spocDetails.centerId,
    name: spocDetails.name,
    email: spocDetails.email,
    phone: spocDetails.phone,
    type: spocDetails.type,
    sendWhatsApp: spocDetails.sendWhatsApp ? 1 : 0,
    sendEmail: spocDetails.sendEmail ? 1 : 0,
    sendCC: spocDetails.sendCC ? 1 : 0,
    sendSMS: spocDetails.sendSMS ? 1 : 0,
    sendCall: spocDetails.sendCall ? 1 : 0,
    commType: spocDetails.commType,
  };
  return axios
    .post(`${config.apiBaseUrl}/network/practice/center/spoc`, body, {
      headers,
    })
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function addEmpaneledUser(empaneledUserDetails) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  const body = {
    centerId: empaneledUserDetails.centerId,
    empaneledUserId: empaneledUserDetails.empaneledUserId,
    source: empaneledUserDetails.source,
    onboardingReqId: empaneledUserDetails.onboardingReqId,
  };
  return axios
    .post(`${config.apiBaseUrl}/network/practice/center/empaneled-user`, body, {
      headers,
    })
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = 'Please Enter valid user id';
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function addNetworkEscalationSpoc(spocDetails, centerId) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  const body = {
    name: spocDetails.name,
    email: spocDetails.email,
    phone: spocDetails.phone,
    escalationLevel: spocDetails.escalationLevel,
    region: spocDetails.region,
  };
  return axios
    .post(
      `${config.apiBaseUrl}/network/practice/center/${centerId}/escalation-matrix-spoc`,
      body,
      {
        headers,
      }
    )
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function removeNetworkEscalationSpoc(spocId, centerId) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  return axios
    .delete(
      `${config.apiBaseUrl}/network/practice/center/${centerId}/escalation-matrix-spoc/${spocId}`,
      {
        headers,
      }
    )
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function removeNetworkEmpaneledUser(empaneledUserId, centerId) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  return axios
    .delete(
      `${config.apiBaseUrl}/network/practice/center/${centerId}/empaneled-users/${empaneledUserId}`,
      {
        headers,
      }
    )
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function fetchEscalationSpoc(centerId) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  return axios
    .get(
      `${config.apiBaseUrl}/network/practice/center/${centerId}/escalation-matrix-spoc`,
      {
        headers,
      }
    )
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function fetchEmpaneledUsers(centerId) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  return axios
    .get(
      `${config.apiBaseUrl}/network/practice/center/${centerId}/empaneled-users`,
      {
        headers,
      }
    )
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function fetchUserDetails(userId, phone) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  return axios
    .get(
      `${config.apiBaseUrl}/network/practice/center/empaneled-users/userDetails`,
      {
        headers,
        params: {
          userId,
          phone,
        },
      }
    )
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function updateNetworkSpoc(spocDetails) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  const body = {
    spocId: spocDetails.spocId,
    name: spocDetails.name,
    email: spocDetails.email,
    phone: spocDetails.phone,
    type: spocDetails.type,
    sendWhatsApp: spocDetails.sendWhatsApp ? 1 : 0,
    sendEmail: spocDetails.sendEmail ? 1 : 0,
    sendCC: spocDetails.sendCC ? 1 : 0,
    sendSMS: spocDetails.sendSMS ? 1 : 0,
    sendCall: spocDetails.sendCall ? 1 : 0,
    commType: spocDetails.commType,
  };
  return axios
    .put(`${config.apiBaseUrl}/network/practice/center/spoc`, body, {
      headers,
    })
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function removeNetworkSpoc(spocId) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  const body = {
    spocId,
  };
  return axios
    .delete(`${config.apiBaseUrl}/network/practice/center/spoc`, {
      headers,
      data: body,
      json: true,
    })
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function createNetworkCenterProfile(centerDetails) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  return axios
    .post(`${config.apiBaseUrl}/network/practice/center`, centerDetails, {
      headers,
    })
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function createAddressCenterLatLong(latitude, longitude) {
  const { authToken } = store.getState().user;

  const headers = {
    authorization: authToken,
  };
  console.log(latitude, longitude);
  return axios
    .post(
      `${config.apiBaseUrl}/network/practice/addresscenter`,
      {
        latitude,
        longitude,
      },
      {
        headers,
      }
    )
    .then((res) => {
      // console.log(res, 'latlong address')
      return res;
    })
    .catch((err) => {
      console.log(err);
      showError(err);
      throw err;
    });
}

export function fetchBankDetailsFromIfscCode(ifscCode) {
  return axios
    .get(`https://ifsc.razorpay.com/${ifscCode}`)
    .then((res) => {
      console.log(res, 'bank details');
      return res;
    })
    .catch((err) => {
      console.log(err);
      showError(err);
      throw err;
    });
}

export function changeNetworkCenterStatus(centerId, active, requestId, remark, approvedAt) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  return axios
    .post(
      `${config.apiBaseUrl}/network/practice/center/status`,
      {
        centerId,
        active,
        requestId,
        remark,
        approvedAt,
      },
      {
        headers,
      }
    )
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        responseBody.body = { message: 'success' };
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function makeCenterStatusActive(centerId, active, requestId, remark, approvedAt) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  return axios
    .post(
      `${config.apiBaseUrl}/network/practice/center/setStatus`,
      {
        centerId,
        active,
        requestId,
        remark,
        approvedAt,
      },
      {
        headers,
      }
    )
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        responseBody.body = { message: 'success' };
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function updateNetworkCenterProfile(centerDetails) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  return axios
    .put(`${config.apiBaseUrl}/network/practice/center`, centerDetails, {
      headers,
    })
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function updateNetworkCenterEmpanelment(centerDetails) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  return axios
    .put(
      `${config.apiBaseUrl}/network/practice/center/changeEmpanellmentStatus`,
      centerDetails,
      {
        headers,
      }
    )
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function updateNetworkCenterEmpanelUserStatus(centerDetails) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  return axios
    .put(
      `${config.apiBaseUrl}/network/practice/center/changeEmpanelUserStatus`,
      centerDetails,
      {
        headers,
      }
    )
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function fetchLastSanitisationSpoc(centerId) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  return axios
    .get(
      `${config.apiBaseUrl}/network/practice/center/sanitisation-spoc/${centerId}`,
      {
        headers,
      }
    )
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function updateDiscountForAllSpecialists(discountPayload) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  return axios
    .put(`${config.apiBaseUrl}/network/practice/discount`, discountPayload, {
      headers,
    })
    .then((resp) => {
      return resp;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function updateAppointmentType(appointmentPayload) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  return axios
    .put(
      `${config.apiBaseUrl}/network/practice/doctor-practice/appointment-type`,
      appointmentPayload,
      {
        headers,
      }
    )
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function updateOnCallBasis(appointmentPayload) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  return axios
    .put(
      `${config.apiBaseUrl}/network/practice/doctor-practice/on-call`,
      appointmentPayload,
      {
        headers,
      }
    )
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function getCentersWithBeneficiary(networkCenterId) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  return axios
    .get(
      `${config.apiBaseUrl}/network/network-center/get-centers-with-beneficiary`,
      {
        headers,
        params: {
          centerId: networkCenterId,
        },
      }
    )
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}
export function submitNetworkCenterFulfilment(
  networkCenterId,
  fulfilmentDetails,
  preferredPaymentMode,
  visitSpoc,
  masterHospitalId,
  tdsPercentage,
  billingEntityId,
  maternityDiscount,
  remarks,
  updateAll,
  updateSameDetail,
  onResponseReceived,
  onResponseComplete,
  onResponseError
) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
    'content-type': 'application/json',
  };
  const body = {
    networkCenterId,
    preferredPaymentMode,
    fulfilments: fulfilmentDetails,
    visitSpoc,
    remarks,
    updateAll,
    masterHospitalId,
    tdsPercentage,
    billingEntityId,
    maternityDiscount,
    requestType: 'center-fulfilment-approval-request',
  };

  if (updateSameDetail) {
    jsonpipe.flow(`${config.apiBaseUrl}/network/network-center/fulfilments`, {
      delimiter: '\r\n',
      success: onResponseReceived,
      complete: onResponseComplete,
      error: onResponseError,
      method: 'POST',
      headers,
      data: JSON.stringify(body),
      disableContentType: true,
    });
  } else {
    jsonpipe.flow(
      `${config.apiBaseUrl}/network/network-center/fulfilments-approvel-request`,
      {
        delimiter: '\r\n',
        success: onResponseReceived,
        complete: onResponseComplete,
        error: onResponseError,
        method: 'POST',
        headers,
        data: JSON.stringify(body),
        disableContentType: true,
      }
    );
  }
  // jsonpipe.flow(`${config.apiBaseUrl}/network/network-center/fulfilments-approvel-request`, {
  //   delimiter: '\r\n',
  //   success: onResponseReceived,
  //   complete: onResponseComplete,
  //   error: onResponseError,
  //   method: 'POST',
  //   headers,
  //   data: JSON.stringify(body),
  //   disableContentType: true,
  // });
  // return axios
  //   .post(`${config.apiBaseUrl}/network/network-center/fulfilments`, body, {
  //     headers,
  //   })
  //   .then(resp => {
  //     const responseBody = resp.data;
  //     if (
  //       responseBody.message === 'success' ||
  //       responseBody.message === 'warning'
  //     ) {
  //       return responseBody;
  //     }
  //     const err = new Error('Invalid response');
  //     err.data = responseBody;
  //     throw err;
  //   })
  //   .catch(err => {
  //     console.log({ err, response: err.response });
  //     showError(err);
  //     throw err;
  //   });
}

export function submitDoctorFulfilment(
  doctorId,
  fulfilmentDetails,
  preferredPaymentMode,
  visitSpoc,
  remarks
) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  const body = {
    doctorId,
    forDoctor: true,
    preferredPaymentMode,
    fulfilments: fulfilmentDetails,
    visitSpoc,
    remarks,
  };
  return axios
    .post(`${config.apiBaseUrl}/network/network-center/fulfilments`, body, {
      headers,
    })
    .then((resp) => {
      const responseBody = resp.data;
      if (
        responseBody.message === 'success' ||
        responseBody.message === 'warning'
      ) {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function refreshDoctorProfile(doctorId) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };

  const body = {
    networkId: doctorId,
    type: 'doctor',
  };
  return axios
    .post(`${config.apiBaseUrl}/network/practice/refresh`, body, {
      headers,
    })
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function removeProfilePic(doctorId) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  return axios
    .delete(
      `${config.apiBaseUrl}/network/practice/doctor-basic-profile/${doctorId}/profileImage`,
      {
        headers,
      }
    )
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function removeCenterProfilePic(centerId) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  return axios
    .delete(
      `${config.apiBaseUrl}/network/practice/center/${centerId}/profileImage`,
      {
        headers,
      }
    )
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function getNotificationList() {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };

  return axios
    .get(`${config.apiBaseUrl}/network/comments`, {
      params: {
        t: 'network',
      },
      headers,
    })
    .then((resp) => {
      const responseBody = resp.data;
      if (
        responseBody.message === 'success' ||
        responseBody.message === 'noComments'
      ) {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function changeCenterStatus(centerId, centerStatus) {
  const { authToken } = store.getState().user;
  console.log({ authToken }, 'Auth Token');
  const headers = {
    authorization: authToken,
  };
  const data = {
    centerId,
    centerStatus,
  };
  const params = {};
  return axios({
    url: `${config.apiBaseUrl}/network/center/change-onboarding-status`,
    method: 'POST',
    headers,
    params,
    data,
  })
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function getDocuments(doctorId, centerId) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  const params = {
    doctorId,
    centerId,
  };
  const data = {};
  return axios({
    url: `${config.apiBaseUrl}/network/documents`,
    method: 'GET',
    headers,
    params,
    data,
  })
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function getMouDocuments(mouLogId) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  const params = {
    mouLogId,
  };
  const data = {};
  return axios({
    url: `${config.apiBaseUrl}/network/mou/documents`,
    method: 'GET',
    headers,
    params,
    data,
  })
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function uploadDocument(
  doctorId,
  centerId,
  type,
  file,
  onUploadProgress
) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  const formData = new FormData();
  formData.append('file', file);
  if (doctorId) {
    formData.append('doctorId', doctorId);
  }
  if (centerId) {
    formData.append('centerId', centerId);
  }
  formData.append('type', type);
  return axios
    .post(`${config.apiBaseUrl}/network/documents`, formData, {
      headers,
      onUploadProgress,
    })
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      } else {
        const err = new Error('Invalid response');
        err.data = responseBody;
        showError(err);
        throw err;
      }
    })
    .catch((err) => {
      showError(err);
      throw err;
    });
}

export function uploadCSVDoctorDocument(
  centerId,

  file,
  onUploadProgress
) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  const formData = new FormData();
  formData.append('file', file);

  formData.append('centerId', centerId);

  return axios
    .post(`${config.apiBaseUrl}/network/uploadDoctorsCSV`, formData, {
      headers,
      onUploadProgress,
    })
    .then((resp) => {
      const responseBody = resp;
      if (responseBody.data.result.response) {
        console.log(responseBody.data.result.response, 'successfuly stored');
        return responseBody.data.result.response;
      } else {
        const err = new Error('Invalid response');
        err.data = responseBody;
        showError(err);
        throw err;
      }
    })
    .catch((err) => {
      showError(err);
      throw err;
    });
}

export function uploadMouDocument(centerId, type, file, onUploadProgress) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  const formData = new FormData();
  formData.append('file', file);
  if (centerId) {
    formData.append('centerId', centerId);
  }
  formData.append('type', type);
  return axios
    .post(`${config.apiBaseUrl}/network/mou/documents`, formData, {
      headers,
      onUploadProgress,
    })
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      } else {
        const err = new Error('Invalid response');
        err.data = responseBody;
        showError(err);
        throw err;
      }
    })
    .catch((err) => {
      showError(err);
      throw err;
    });
}

export function removeDocument(documentId) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  return axios
    .delete(`${config.apiBaseUrl}/network/documents/${documentId}`, {
      headers,
    })
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      } else {
        const err = new Error('Invalid response');
        err.data = responseBody;
        showError(err);
        throw err;
      }
    })
    .catch((err) => {
      showError(err);
      throw err;
    });
}

export function changeStatusOfMoU(mouId, status) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };

  const body = {
    mouId,
    status,
  };
  return axios
    .put(`${config.apiBaseUrl}/network/practice/center/mou/status`, body, {
      headers,
    })
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      } else {
        const err = new Error('Invalid response');
        err.data = responseBody;
        showError(err);
        throw err;
      }
    })
    .catch((err) => {
      showError(err);
      throw err;
    });
}

export function removeMouDocument(documentId) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  return axios
    .delete(`${config.apiBaseUrl}/network/mou/documents/${documentId}`, {
      headers,
    })
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      } else {
        const err = new Error('Invalid response');
        err.data = responseBody;
        showError(err);
        throw err;
      }
    })
    .catch((err) => {
      showError(err);
      throw err;
    });
}

export function updateNetworkCenterSettlements(centerId, fulfilments) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  return axios
    .post(
      `${config.apiBaseUrl}/network/network-center/settlement`,
      {
        networkCenterId: centerId,
        fulfilments,
      },
      {
        headers,
      }
    )
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function verifyUpi(upi) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  return axios
    .post(
      `${config.verificationUrl}/verification/verify-upi`,
      { upi },
      { headers }
    )
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      if (responseBody.errsubCode === '200') {
        const err = new Error('Invalid reponse');
        err.data = {
          errorMessage: 'Invalid UPI',
        };
        showError(err);
        return responseBody;
      }
      if (responseBody.errsubCode !== '200') {
        const err = new Error('Invalid response');
        err.data = responseBody;
        showError(err);
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function verifyBankAccount(bankAccount, ifsc) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  return axios
    .post(
      `${config.verificationUrl}/verification/verify-bankAccount`,
      { bankAccount, ifsc },
      { headers }
    )
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      if (responseBody.errsubCode === '200') {
        const err = new Error('Invalid reponse');
        err.data = responseBody;
        showError(err);
        return responseBody;
      }
      if (responseBody.errsubCode !== '200') {
        const err = new Error('Invalid response');
        err.data = responseBody;
        showError(err);
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function verifyPan(pan) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  return axios
    .post(
      `${config.verificationUrl}/verification/verify-pan`,
      { pan },
      { headers }
    )
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      if (responseBody.message === 'failure') {
        const err = new Error('Invalid response');
        err.data = responseBody;
        showError(err);
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function verifyName(pan, bankAccount, ifsc) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  return axios
    .post(
      `${config.verificationUrl}/verification/verify-name`,
      { pan, bankAccount, ifsc },
      { headers }
    )
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      if (
        responseBody.message === 'failure' &&
        responseBody.errorMessage ===
          'Name mismatch between PAN and bank records.'
      ) {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function updateShowSameDaySlotsStatus(details) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  return axios
    .post(
      `${config.apiBaseUrl}/network/practice/center/update-same-day-slots-status`,
      details,
      {
        headers,
      }
    )
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}
export function updateTimeRangeStatus(details) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  return axios
    .post(
      `${config.apiBaseUrl}/network/practice/center/set-lead-time`,
      details,
      {
        headers,
      }
    )
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function modifyAndFetchCenterFulfilments(billingEntityId, centerId) {
  console.log(`modifyAndFetchCenterFulfilments ${billingEntityId}`);
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  return axios
    .get(
      `${config.apiBaseUrl}/network/practice/center-fulfilment?cortisBillingEntityId=${billingEntityId}&centerId=${centerId}`,
      {
        headers,
      }
    )
    .then((resp) => {
      console.log(`response fulfilments ${resp}`);
      return resp;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function fetchCenterFulfilments(centerId) {
  console.log(`fetchCenterFulfilments/billingEntityId ${centerId}`);
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  return axios
    .get(
      `${config.apiBaseUrl}/network/practice/get-center-fulfilment?centerId=${centerId}`,
      {
        headers,
      }
    )
    .then((resp) => {
      console.log(`response fulfilments ${resp}`);
      return resp;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function removeNetworkCenterFulfilmentDetails(centerId) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  const body = {
    centerId,
  };
  return axios
    .delete(`${config.apiBaseUrl}/network/practice/remove-center-fulfilment`, {
      headers,
      data: body,
      json: true,
    })
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function fetchDoctorProfile(doctorId) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  return axios
    .get(`${config.apiBaseUrl}/network/doctors/${doctorId}/profile`, {
      headers,
    })
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function fetchDoctorsDetailForCenterByCSV(centerId) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  return axios
    .get(`${config.apiBaseUrl}/network/center/${centerId}/doctors/detail`, {
      headers,
    })
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export async function uploadDoctorsDetailForCenterByCSV(
  file,
  fileName,
  centerId
) {
  try {
    const { authToken } = store.getState().user;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('fileName', fileName);
    formData.append('centerId', centerId);
    const response = await axios.post(
      `${config.apiBaseUrl}/network/center/upload/doctors/detail`,
      formData,
      {
        headers: {
          Authorization: authToken,
        },
      }
    );

    if (response.status == 200) {
      return response.data;
    }
    const err = new Error('Invalid response');
    err.data = response.data;
    throw err;
  } catch (err) {
    console.error('Upload failed:', err.response || err);
    throw err;
  }
}

export const uploadImageAttachment = (
  file,
  doctorId,
  consultationId,
  userId,
  requestId,
  onUploadProgress,
  authToken
) => {
  const headers = {
    authorization: authToken,
  };

  const formData = new FormData();
  formData.append('file', file);
  formData.append('consultationId', consultationId);
  formData.append('doctorId', doctorId);
  formData.append('userId', userId);
  formData.append('requestId', requestId);
  formData.append('preview', file);
  formData.append('prescription', 1);

  return axios
    .post(
      `${config.apiBaseUrl}/hospital/users/${userId}/upload/${requestId}`,
      formData,
      {
        headers,
        onUploadProgress,
      }
    )
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      } else {
        const err = new Error('Invalid response');
        err.data = responseBody;
        throw err;
      }
    })
    .catch((err) => {
      throw err;
    });
};

export function createApporvalRequestForDiscountUpdate(discountPayload) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  return axios
    .post(
      `${config.apiBaseUrl}/network/practice/discount/approval`,
      discountPayload,
      {
        headers,
      }
    )
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function checkCenterNetworkActivity(centerId) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  return axios
    .get(
      `${config.apiBaseUrl}/network/practice/check-network-activity?centerId=${centerId}`,
      {
        headers,
      }
    )
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}

export function centerActivationForPrebook(body) {
  const { authToken } = store.getState().user;
  const headers = {
    authorization: authToken,
  };
  return axios
    .post(
      `${config.apiBaseUrl}/network/practice/center-activation-for-prebook`,
      body,
      {
        headers,
      }
    )
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      }
      const err = new Error('Invalid response');
      err.data = responseBody;
      throw err;
    })
    .catch((err) => {
      console.log({ err, response: err.response });
      showError(err);
      throw err;
    });
}
